import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Department, Position } from '../../types/user';
import { getDepartments } from '../../services/userApi';

interface EditPositionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (id: number, positionData: {
        name: string;
        department_id: number;
    }) => void;
    position: Position | null;
}

export default function EditPositionModal({ isOpen, onClose, onSubmit, position }: EditPositionModalProps) {
    const [departments, setDepartments] = useState<Department[]>([]);
    const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);
    const [formData, setFormData] = useState<{
        name: string;
        department_id: number;
    }>({
        name: '',
        department_id: 0
    });

    useEffect(() => {
        if (position) {
            setFormData({
                name: position.name,
                department_id: position.department_id
            });
        }
    }, [position]);

    useEffect(() => {
        if (isOpen) {
            fetchDepartments();
        }
    }, [isOpen]);

    const fetchDepartments = async () => {
        try {
            setIsLoadingDepartments(true);
            const response = await getDepartments();
            setDepartments(response.data);
        } catch (error) {
            console.error('Failed to fetch departments:', error);
        } finally {
            setIsLoadingDepartments(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (position && formData.department_id !== 0) {
            onSubmit(position.id, formData);
        }
        onClose();
    };

    if (!isOpen || !position) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md p-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Edit Position</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                        <X size={20} className="text-gray-600 dark:text-gray-400" />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Position Name</label>
                            <input
                                type="text"
                                required
                                className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 dark:bg-gray-700 dark:text-white"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Department</label>
                            <select
                                required
                                className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 dark:bg-gray-700 dark:text-white"
                                value={formData.department_id}
                                onChange={(e) => setFormData({ ...formData, department_id: Number(e.target.value) })}
                            >
                                <option value={0}>Select a department</option>
                                {departments.map((department) => (
                                    <option key={department.id} value={department.id}>
                                        {department.name}
                                    </option>
                                ))}
                            </select>
                            {isLoadingDepartments && <span className="text-sm text-gray-500 dark:text-gray-400">Loading departments...</span>}
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600"
                        >
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
} 